/**
 * The application shell (SPEC §9.1, §9.2).
 *
 * Loads `/api/me` once and holds it for the whole session: it is the basis of
 * every piece of navigation the SPA renders. That is presentation only — every
 * report path re-resolves access server-side (§7.2), and this component must
 * never be mistaken for the thing that enforces it.
 *
 * Departments are the **home** (the `/` grid); the left panel holds the
 * **reports** of the department you are in, and is absent on the home grid and
 * on `/admin`. The rail is data-driven — it reads the department's report list
 * from the API, never a hardcoded list — so adding a report still needs no
 * change here (the M4 property).
 *
 * The shell also owns the department accent. `--accent` is set here from the
 * active route and read by the rail's active marker, the focus ring, the KPI
 * hairline, and the chart primary series, so wayfinding is one value rather than
 * several places that can drift apart.
 */

import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'
import { Link, Outlet, useParams } from 'react-router'

import { api, ApiError, setCsrfToken } from '../lib/api'
import { departmentAccent } from '../lib/charts'
import { ME_STALE_MS } from '../lib/session'
import { ThemeToggle } from '../components/ThemeToggle'

/**
 * The left panel: the current department's reports (SPEC §9.1).
 *
 * Shown only inside a department, so `/`, `/admin` and `/no-access` — none of
 * which carry a `:department` param — render full width. The report list is the
 * same query `Department.tsx` runs, so React Query serves both from one request.
 */
function ReportRail({
  department,
  departmentName,
  activeSlug,
}: {
  department: string
  departmentName: string
  activeSlug: string | undefined
}) {
  const { data } = useQuery({
    queryKey: ['department-reports', department],
    queryFn: () => api.departmentReports(department),
  })

  return (
    <nav
      aria-label="Reports"
      className="hidden w-60 shrink-0 border-r border-[var(--color-rule)] px-3 py-4 md:block"
    >
      <Link
        to="/"
        className="mb-3 inline-flex items-center gap-1 text-[length:var(--text-xs)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
      >
        <ArrowLeft aria-hidden className="size-3.5" />
        Departments
      </Link>

      <p className="mb-2 flex items-center gap-2 text-[length:var(--text-sm)] font-semibold">
        <span aria-hidden className="h-4 w-0.5 rounded-full bg-[var(--accent)]" />
        {departmentName}
      </p>

      <ul className="space-y-0.5">
        {(data ?? []).map((report) => {
          const isActive = report.slug === activeSlug
          return (
            <li key={report.slug}>
              <Link
                to={`/d/${department}/r/${report.slug}`}
                aria-current={isActive ? 'page' : undefined}
                className={`flex flex-col gap-0.5 rounded-[var(--radius-md)] px-2.5 py-2 hover:bg-[var(--color-rule)]/30 ${
                  isActive ? 'bg-[var(--color-rule)]/30 shadow-[inset_2px_0_0_var(--accent)]' : ''
                }`}
              >
                <span className="text-[length:var(--text-sm)] font-medium text-[var(--color-ink)]">
                  {report.title}
                </span>
                <span className="line-clamp-2 text-[length:var(--text-2xs)] leading-snug text-[var(--color-ink-muted)]">
                  {report.summary}
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

function SignInPage({ loginUrl }: { loginUrl: string }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--color-page)] px-4">
      <section className="w-full max-w-md rounded-[var(--radius-md)] border border-[var(--color-rule)] bg-[var(--color-surface-raised)] p-7 shadow-sm">
        <div className="space-y-1">
          <h1 className="text-[length:var(--text-2xl)] font-semibold tracking-tight">
            Reporting Portal
          </h1>
          <p className="text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
            Sign in with your Sun Orchard Microsoft account.
          </p>
        </div>
        <a
          href={loginUrl}
          className="mt-6 flex h-11 w-full items-center justify-center rounded-[var(--radius-md)] bg-[#2563eb] px-4 text-[length:var(--text-sm)] font-medium text-white hover:bg-[#1d4ed8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563eb]"
        >
          Sign in with Microsoft
        </a>
      </section>
    </main>
  )
}

export function Root() {
  const { data: me, error } = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    staleTime: ME_STALE_MS,
    retry: false,
  })
  const { department: activeDepartment, slug: activeSlug } = useParams()

  useEffect(() => {
    if (me !== undefined) setCsrfToken(me.csrf_token)
  }, [me])

  useEffect(() => {
    // One place sets the accent; everything else reads var(--accent). The value
    // is the *served* accent from `/api/me` (§9.2, M8), so an admin recolour
    // reaches the rail stripe, focus ring, KPI hairline and the bespoke charts
    // — the seed map is only a fallback until `me` has loaded or for a key it
    // does not carry.
    const served = me?.departments.find((entry) => entry.key === activeDepartment)?.accent
    document.documentElement.style.setProperty(
      '--accent',
      activeDepartment === undefined
        ? 'var(--color-ink)'
        : (served ?? departmentAccent(activeDepartment)),
    )
  }, [activeDepartment, me])

  const departmentName =
    me?.departments.find((entry) => entry.key === activeDepartment)?.name ??
    activeDepartment ??
    ''
  const loginUrl =
    error instanceof ApiError && error.isUnauthenticated ? (error.loginUrl ?? '/auth/login') : null

  if (loginUrl !== null) {
    return <SignInPage loginUrl={loginUrl} />
  }

  return (
    <div className="min-h-dvh">
      <header className="flex items-center gap-4 border-b border-[var(--color-rule)] bg-[var(--color-surface-raised)] px-4 py-2.5">
        <Link to="/" className="text-[length:var(--text-base)] font-semibold tracking-tight">
          Reporting Portal
        </Link>
        <div className="ml-auto flex items-center gap-3 text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
          <ThemeToggle />
          {me !== undefined && <span>{me.user.display_name}</span>}
          {me?.is_admin === true && (
            <Link to="/admin" className="hover:text-[var(--color-ink)]">
              Admin
            </Link>
          )}
          <button
            type="button"
            className="hover:text-[var(--color-ink)]"
            onClick={() => {
              // Let the browser navigate to Microsoft's logout page. Fetch
              // cannot reliably follow that cross-site sign-out flow.
              void api.logout().then((result) => {
                window.location.href = result?.logout_url ?? '/auth/login'
              }).catch(() => {
                window.location.href = '/auth/login'
              })
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="flex">
        {/* The report rail appears only inside a department. */}
        {activeDepartment !== undefined && (
          <ReportRail
            department={activeDepartment}
            departmentName={departmentName}
            activeSlug={activeSlug}
          />
        )}
        <main className="min-w-0 flex-1 px-4 py-6 md:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
